import type { InviteRoleId } from "./types";

export const WELCOME_TOTAL_STEPS = 6;

export const ROLES = [
  "Owner / Principal",
  "Senior Agent",
  "Negotiator",
  "Analyst",
  "Operations",
  "Legal",
] as const;

export const INDUSTRIES = [
  { id: "sports", label: "Sports", desc: "Endorsements, contracts, NIL" },
  { id: "music", label: "Music", desc: "Publishing, touring, catalog" },
  { id: "entertainment", label: "Film & TV", desc: "Talent reps, packaging" },
  { id: "creator", label: "Creator", desc: "Brand deals, content, IP" },
  { id: "mixed", label: "Mixed roster", desc: "Multiple verticals" },
] as const;

export const TEAM_SIZES = ["Just me", "2–5", "6–20", "21–100", "100+"] as const;

export interface RoleDef {
  id: InviteRoleId;
  label: string;
  desc: string;
  badgeVariant: "default" | "info" | "success" | "warning" | "secondary";
  dotClassName: string;
}

export const ROLE_DEFS: RoleDef[] = [
  {
    id: "owner",
    label: "Owner",
    desc: "Full access · billing · org settings",
    badgeVariant: "default",
    dotClassName: "bg-primary",
  },
  {
    id: "admin",
    label: "Admin",
    desc: "Full deal access · manage members",
    badgeVariant: "info",
    dotClassName: "bg-info",
  },
  {
    id: "negotiator",
    label: "Negotiator",
    desc: "Draft contracts · run deals",
    badgeVariant: "success",
    dotClassName: "bg-success",
  },
  {
    id: "analyst",
    label: "Analyst",
    desc: "Read-only · build reports",
    badgeVariant: "warning",
    dotClassName: "bg-warning",
  },
  {
    id: "guest",
    label: "Guest",
    desc: "Single client / single deal access",
    badgeVariant: "secondary",
    dotClassName: "bg-muted-foreground",
  },
];

export interface ConnectSource {
  id: string;
  brand: string;
  sub: string;
  icon: "mail" | "cal" | "card" | "bank" | "doc";
  required: boolean;
  detail: string;
}

export const CONNECT_SOURCES: ConnectSource[] = [
  {
    id: "gmail",
    brand: "Gmail",
    sub: "Pull deal emails into your inbox",
    icon: "mail",
    required: true,
    detail: "Read-only access · we never send on your behalf",
  },
  {
    id: "gcal",
    brand: "Google Calendar",
    sub: "Sync meetings + deal milestones",
    icon: "cal",
    required: false,
    detail: "Read + write · auto-block holds",
  },
  {
    id: "stripe",
    brand: "Stripe",
    sub: "Track invoices, payouts, commissions",
    icon: "card",
    required: false,
    detail: "Read-only · payouts stay in your account",
  },
  {
    id: "plaid",
    brand: "Plaid (bank)",
    sub: "Reconcile incoming wires & ACH",
    icon: "bank",
    required: false,
    detail: "Bank-grade encryption via Plaid",
  },
  {
    id: "docusign",
    brand: "DocuSign",
    sub: "Sync envelopes & signed contracts",
    icon: "doc",
    required: false,
    detail: "Read + send · auto-attach to deals",
  },
];

export interface SampleIncomingDeal {
  from: string;
  org: string;
  subject: string;
  client: string;
  tag: string;
  amount: string;
}

export const SAMPLE_INCOMING_DEALS: SampleIncomingDeal[] = [
  {
    from: "Marcus Webb",
    org: "Roc Nation",
    subject: "Jay-Z Publishing Rights — Catalog Acquisition Proposal",
    client: "jay-z",
    tag: "Music",
    amount: "$85M",
  },
  {
    from: "Dominique Azari",
    org: "Fenty Group",
    subject: "Savage x Fenty x Nike Collab — Final Term Sheet",
    client: "rihanna",
    tag: "Brand deal",
    amount: "$42M",
  },
  {
    from: "Klutch Sports Legal",
    org: "Klutch Sports",
    subject: "LeBron x Nike Lifetime Deal — Royalty Amendment",
    client: "lebron",
    tag: "Endorsement",
    amount: "$120M",
  },
  {
    from: "Vanessa Bryant",
    org: "Mamba Sports",
    subject: "Kobe Nike Reacquisition — Licensing Structure Update",
    client: "kobe",
    tag: "Licensing",
    amount: "$28M",
  },
  {
    from: "Parkwood Legal",
    org: "Parkwood",
    subject: "Renaissance World Tour — Merch & Venue Rights Amendment",
    client: "beyonce",
    tag: "Touring",
    amount: "$58M",
  },
  {
    from: "Larry Miller",
    org: "Jordan Brand",
    subject: "Travis Scott — Sneaker Continuation Offer",
    client: "travis",
    tag: "Brand deal",
    amount: "$45M",
  },
  {
    from: "Brian Grevy",
    org: "adidas",
    subject: "Travis Scott — Yeezy Successor Line · Final Offer",
    client: "travis",
    tag: "Brand deal",
    amount: "$72M",
  },
  {
    from: "CAA Sports",
    org: "CAA",
    subject: "Patrick Mahomes — State Farm Renewal · 5-yr Term",
    client: "mahomes",
    tag: "Endorsement",
    amount: "$95M",
  },
  {
    from: "Shaboozey Mgmt",
    org: "EMPIRE",
    subject: "Sync Licensing — Apple TV+ Pilot · 4 tracks",
    client: "shab",
    tag: "Sync",
    amount: "$1.2M",
  },
  {
    from: "Wasserman Music",
    org: "Wasserman",
    subject: "Olivia Rodrigo — North America 2026 Routing & Holds",
    client: "olivia",
    tag: "Touring",
    amount: "$38M",
  },
  {
    from: "Range Media",
    org: "Range",
    subject: "Zendaya — Louis Vuitton Global Ambassadorship Renewal",
    client: "zendaya",
    tag: "Brand deal",
    amount: "$18M",
  },
  {
    from: "Excel Sports",
    org: "Excel",
    subject: "Caitlin Clark — Wilson NIL Extension & Equity Grant",
    client: "caitlin",
    tag: "NIL",
    amount: "$8.5M",
  },
];
